import {
  Extension,
  findChildren,
  combineTransactionSteps,
  getChangedRanges,
  findChildrenInRange,
  getMarksBetween,
} from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Slice, Fragment } from 'prosemirror-model';
import { v4 } from 'uuid';

/**
 * Removes duplicated values within an array.
 * Supports numbers, strings and objects.
 */
function removeDuplicates(array, by = JSON.stringify) {
  const seen = {};
  return array.filter(item => {
    const key = by(item);
    return Object.prototype.hasOwnProperty.call(seen, key)
      ? false
      : (seen[key] = true);
  });
}

/**
 * Returns a list of duplicated items within an array.
 */
function findDuplicates(items) {
  const filtered = items.filter((el, index) => items.indexOf(el) !== index);
  const duplicates = removeDuplicates(filtered);
  return duplicates;
}

const uniqueIDType = 'uniqueID';

const UniqueID = Extension.create({
  name: uniqueIDType,
  // we’ll set a very high priority to make sure this runs first
  // and is compatible with `appendTransaction` hooks of other extensions
  priority: 10000,
  addOptions() {
    return {
      attributeName: 'id',
      types: [],
      generateID: (type: string) => `${type}-${v4()}`,
      filterTransaction: null,
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          [this.options.attributeName]: {
            default: null,
            parseHTML: element =>
              element.getAttribute(`${this.options.attributeName}`),
            renderHTML: attributes => {
              if (!attributes[this.options.attributeName]) {
                return {};
              }
              return {
                [`${this.options.attributeName}`]: attributes[
                  this.options.attributeName
                ],
              };
            },
          },
        },
      },
    ];
  },
  // check initial content for missing ids
  onCreate() {
    const { view, state } = this.editor;
    const { tr, doc } = state;
    const { types, attributeName, generateID } = this.options;
    const nodesWithoutId = findChildren(doc, node => {
      return (
        types.includes(node.type.name) && node.attrs[attributeName] === null
      );
    });
    nodesWithoutId.forEach(({ node, pos }) => {
      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        [attributeName]: generateID(node.type.name),
      });
    });
    view.dispatch(tr);
  },
  addProseMirrorPlugins() {
    let dragSourceElement: HTMLElement | null = null;
    let transformPasted = false;
    return [
      new Plugin({
        key: new PluginKey('uniqueID'),
        appendTransaction: (transactions, oldState, newState) => {
          const docChanges =
            transactions.some(transaction => transaction.docChanged) &&
            !oldState.doc.eq(newState.doc);
          const filterTransactions =
            this.options.filterTransaction &&
            transactions.some(tr => {
              let _a, _b;
              return !((_b = (_a = this.options).filterTransaction) === null ||
              _b === void 0
                ? void 0
                : _b.call(_a, tr));
            });
          if (!docChanges || filterTransactions) {
            return;
          }
          const { tr } = newState;
          const { types, attributeName, generateID } = this.options;
          const transform = combineTransactionSteps(oldState.doc, transactions);
          const { mapping } = transform;
          // get changed ranges based on the old state
          const changes = getChangedRanges(transform);
          changes.forEach(({ newRange }) => {
            const newNodes = findChildrenInRange(
              newState.doc,
              newRange,
              node => {
                return types.includes(node.type.name);
              },
            );

            const newMarks = getMarksBetween(
              newRange.from,
              newRange.to,
              newState.doc,
            ).filter(mark => types.includes(mark.mark.type.name));

            if (newNodes && newNodes.length) {
              const newIds = newNodes
                .map(({ node }) => node.attrs[attributeName])
                .filter(id => id !== null);
              const duplicatedNewIds = findDuplicates(newIds);

              newNodes.forEach(({ node, pos }) => {
                let _a;
                // instead of checking `node.attrs[attributeName]` directly
                // we look at the current state of the node within `tr.doc`.
                // this helps to prevent adding new ids to the same node
                // if the node changed multiple times within one transaction
                const id =
                  (_a = tr.doc.nodeAt(pos)) === null || _a === void 0
                    ? void 0
                    : _a.attrs[attributeName];
                if (id === null) {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    [attributeName]: generateID(node.type.name),
                  });
                  return;
                }
                // check if the node doesn’t exist in the old state
                const { deleted } = mapping.invert().mapResult(pos);
                const newNode = deleted && duplicatedNewIds.includes(id);
                if (newNode) {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    [attributeName]: generateID(node.type.name),
                  });
                }
              });
            }

            if (newMarks && newMarks.length) {
              newMarks.forEach(({ mark }) => {
                const existingId = mark.attrs[attributeName];
                if (existingId === null) {
                  mark.attrs = {
                    ...mark.attrs,
                    [attributeName]: generateID(mark.type.name),
                  };
                  tr.addMark(newRange.from, newRange.to, mark);
                  return;
                }
              });
            }
          });
          if (!tr.steps.length) {
            return;
          }
          return tr;
        },
        // we register a global drag handler to track the current drag source element
        view(view) {
          const handleDragstart = event => {
            let _a;
            dragSourceElement = ((_a = view.dom.parentElement) === null ||
            _a === void 0
            ? void 0
            : _a.contains(event.target))
              ? view.dom.parentElement
              : null;
          };
          window.addEventListener('dragstart', handleDragstart);
          return {
            destroy() {
              window.removeEventListener('dragstart', handleDragstart);
            },
          };
        },
        props: {
          // `handleDOMEvents` is called before `transformPasted`
          // so we can do some checks before
          handleDOMEvents: {
            // only create new ids for dropped content while holding `alt`
            // or content is dragged from another editor
            drop: (view, event) => {
              let _a;
              if (
                dragSourceElement !== view.dom.parentElement ||
                ((_a = event.dataTransfer) === null || _a === void 0
                  ? void 0
                  : _a.effectAllowed) === 'copy'
              ) {
                dragSourceElement = null;
                transformPasted = true;
              }
              return false;
            },
            // always create new ids on pasted content
            paste: () => {
              transformPasted = true;
              return false;
            },
          },
          // we’ll remove ids for every pasted node
          // so we can create a new one within `appendTransaction`
          transformPasted: slice => {
            if (!transformPasted) {
              return slice;
            }
            const { types, attributeName } = this.options;
            const removeId = fragment => {
              const list: any[] = [];
              fragment.forEach(node => {
                // don’t touch text nodes
                if (node.isText) {
                  list.push(node);
                  return;
                }
                // check for any other child nodes
                if (!types.includes(node.type.name)) {
                  list.push(node.copy(removeId(node.content)));
                  return;
                }
                // remove id
                const nodeWithoutId = node.type.create(
                  {
                    ...node.attrs,
                    [attributeName]: null,
                  },
                  removeId(node.content),
                  node.marks,
                );
                list.push(nodeWithoutId);
              });
              return Fragment.from(list);
            };
            // reset check
            transformPasted = false;
            return new Slice(
              removeId(slice.content),
              slice.openStart,
              slice.openEnd,
            );
          },
        },
      }),
    ];
  },
});

export default { extention: UniqueID, type: uniqueIDType };
//# sourceMappingURL=tiptap-extension-unique-id.esm.js.map
